import { useEffect, useState } from "react";
import { Modal } from "antd";
import {
  FileAddOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import { ChoiceRow } from "../../ChoiceRow";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  posModalStyles as modalStyles,
} from "../../modalPrimitives";

export type InvoicingChoice = "current" | "closed" | "free";

interface InvoicingChoiceModalProps {
  open: boolean;
  onClose: () => void;
  onChoose: (choice: InvoicingChoice) => void;
  /** Предлага ли се „Текуща сметка" (фискална фактура при закриване); false —
   *  редът го няма („Софтуерно", без правото да закрива, локален режим). */
  showCurrent: boolean;
  /** Предлага ли се „Свободна фактура" — правото `free_invoice`; false —
   *  редът го няма (както „Текуща сметка" без правото да закрива). */
  showFree: boolean;
  /** Избраната маса — името и сумата ѝ в пояснението; null — няма избрана. */
  current: { tableName: string; amount: string } | null;
  /** Фискалното устройство на фактурата (`printer_name` от наличността). */
  printerName: string | null;
  /** Защо „Текуща сметка" не може (маса, наличност, грешка); null — може. */
  currentBlockedReason: string | null;
  /** „Проверява се…", докато облакът каже може ли апаратът; null — отговорил е. */
  currentPendingText: string | null;
  /** Защо софтуерната фактура („Закрита сметка" и „Свободна фактура") не
   *  може („Фискално устройство"); null — може. */
  softwareBlockedReason: string | null;
  /** Причината на реда „Закрита сметка", когато е друга (казва пътя, който
   *  съществува); без нея — `softwareBlockedReason`. */
  closedBlockedReason?: string | null;
}

/**
 * „Фактуриране" — редове като при „Депозит": „Текуща сметка" (фискална
 * фактура за избраната маса при закриването: купувач → „Закрий"; само когато
 * се предлага), „Закрита сметка" (софтуерна фактура за вече закрити сметки —
 * `InvoiceBillSelectionModal`) и „Свободна фактура" (без сметка, редовете на
 * ръка — само „По сметка", без бон; само с правото `free_invoice`). Ред, който
 * не може, е неактивен с причината. Един клик избира; на десктоп стрелките
 * местят курсора по възможните редове, Enter избира, Esc затваря.
 */
export function InvoicingChoiceModal({
  open,
  onClose,
  onChoose,
  showCurrent,
  showFree,
  current,
  printerName,
  currentBlockedReason,
  currentPendingText,
  softwareBlockedReason,
  closedBlockedReason,
}: InvoicingChoiceModalProps) {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isDesktop } = useViewport();
  const currentBlocked = !!currentBlockedReason || !!currentPendingText;
  const softwareBlocked = !!softwareBlockedReason;
  // Редовете, по които ходи клавиатурният курсор — в реда на екрана.
  const enabled: InvoicingChoice[] = [
    ...(showCurrent && !currentBlocked ? (["current"] as const) : []),
    ...(softwareBlocked ? [] : (["closed"] as const)),
    ...(showFree && !softwareBlocked ? (["free"] as const) : []),
  ];
  const enabledKey = enabled.join(",");
  const [highlighted, setHighlighted] = useState<InvoicingChoice | null>(null);

  useEffect(() => {
    if (open) setHighlighted((enabledKey.split(",")[0] as InvoicingChoice) || null);
  }, [open, enabledKey]);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const choices = enabledKey ? (enabledKey.split(",") as InvoicingChoice[]) : [];
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      } else if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
        event.preventDefault();
        if (choices.length < 2) return;
        const step = event.key === "ArrowUp" || event.key === "ArrowLeft" ? -1 : 1;
        setHighlighted((choice) => {
          const index = choice ? choices.indexOf(choice) : -1;
          return choices[(index + step + choices.length) % choices.length];
        });
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (highlighted && choices.includes(highlighted)) onChoose(highlighted);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isDesktop, highlighted, enabledKey, onChoose, onClose]);

  const currentHint = current
    ? printerName
      ? t("invoicingChoice.currentHintPrinter", { ...current, printer: printerName })
      : t("invoicingChoice.currentHint", current)
    : t("invoicingChoice.currentHintNoTable");
  const isHighlighted = (choice: InvoicingChoice) =>
    highlighted === choice && enabled.includes(choice);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isMobile ? undefined : 560}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<FileDoneOutlined />}
          title={t("invoicingChoice.title")}
          subtitle={t(showCurrent ? "invoicingChoice.subtitle" : "invoicingChoice.subtitleSoftware")}
          onClose={onClose}
          isNarrow={isNarrow}
        />
        <div className={`flex flex-col gap-2.5 ${isNarrow ? "px-4 py-4" : "px-6 py-5"}`}>
          {showCurrent ? (
            <ChoiceRow
              icon={<FileTextOutlined />}
              kicker={
                current
                  ? t("invoicingChoice.currentKickerTable", { table: current.tableName })
                  : t("invoicingChoice.currentKicker")
              }
              title={t("invoicingChoice.current")}
              description={currentHint}
              highlighted={isHighlighted("current")}
              showEnterHint={isDesktop && isHighlighted("current")}
              blockedReason={currentBlockedReason}
              pendingText={currentBlockedReason ? null : currentPendingText}
              isNarrow={isNarrow}
              onClick={() => onChoose("current")}
            />
          ) : null}
          <ChoiceRow
            icon={<HistoryOutlined />}
            kicker={t("invoicingChoice.closedKicker")}
            title={t("invoicingChoice.closed")}
            description={t("invoicingChoice.closedHint")}
            highlighted={isHighlighted("closed")}
            showEnterHint={isDesktop && isHighlighted("closed")}
            blockedReason={softwareBlockedReason ? (closedBlockedReason ?? softwareBlockedReason) : null}
            isNarrow={isNarrow}
            onClick={() => onChoose("closed")}
          />
          {showFree ? (
            <ChoiceRow
              icon={<FileAddOutlined />}
              kicker={t("invoicingChoice.freeKicker")}
              title={t("invoicingChoice.free")}
              description={t("invoicingChoice.freeHint")}
              highlighted={isHighlighted("free")}
              showEnterHint={isDesktop && isHighlighted("free")}
              blockedReason={softwareBlockedReason}
              isNarrow={isNarrow}
              onClick={() => onChoose("free")}
            />
          ) : null}
        </div>
        <ModalFooter isNarrow={isNarrow}>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
}
